import { AlertCircle, Check, Loader2 } from 'lucide-react';
import { Button } from '@reflex/ui/components/ui/button';
import { Card } from '@reflex/ui/components/ui/card';
import { InlineDocsHint, PanelTitle } from '@reflex/ui/components/layout/Typography';
import { RunloopConnectedAccount } from '@/components/shared/RunloopConnectedAccount';
import type { RunloopAccountSummary } from '@/lib/org-setup-api';

export type SandboxAccountPhase = 'provisioning' | 'ready' | 'failed';

export interface SandboxAccountCardProps {
  phase: SandboxAccountPhase;
  /** Live account behind the org's key. Rendered in the `ready` phase. */
  account?: RunloopAccountSummary | null;
  /**
   * Copy replacing the phase's default explanation. Carries the failure
   * reason in the `failed` phase, and in the `provisioning` phase the reason
   * this is taking a different shape than usual (another request already
   * owns the setup, say).
   */
  message?: string | null;
  /**
   * Whether repeating the request could plausibly help. Offered in both the
   * failed and provisioning phases: waiting on someone else's request must
   * not be a state the user can only escape by reloading.
   */
  canRetry?: boolean;
  onRetry?: () => void;
  isRetrying?: boolean;
  /** Escape hatch offered alongside a failure: paste an existing key. */
  onAddKeyManually?: () => void;
  /** Optional docs slug (with `#anchor`) for the inline hint by the title. */
  docsSlug?: string;
}

const TITLES: Record<SandboxAccountPhase, string> = {
  provisioning: 'Setting up your sandbox',
  ready: 'Sandbox ready',
  failed: 'Sandbox setup did not finish',
};

const DEFAULT_PROVISIONING_MESSAGE =
  'Reflex is creating a Runloop account for this organization and storing its key for you. This usually takes a few seconds.';

const DEFAULT_FAILURE_MESSAGE =
  'Reflex could not finish setting up your sandbox account. Try again in a moment.';

/**
 * Status of the organization's Runloop sandbox account during onboarding.
 *
 * Reflex creates the account and stores its key itself, so onboarding never
 * asks for a `RUNLOOP_API_KEY` and never displays one. This card is the whole
 * user-facing surface of that work: it says the setup is happening, names the
 * account once it lands, and turns a failure into something the user can act
 * on (retry when that can help, otherwise paste a key they already have).
 */
export function SandboxAccountCard({
  phase,
  account = null,
  message = null,
  canRetry = false,
  onRetry,
  isRetrying = false,
  onAddKeyManually,
  docsSlug,
}: SandboxAccountCardProps) {
  const showRetry = canRetry && !!onRetry && phase !== 'ready';
  const showManualKey = phase === 'failed' && !!onAddKeyManually;

  return (
    <Card className="gap-0 p-4" data-testid="sandbox-account-card" data-phase={phase}>
      <div className="flex min-w-0 items-start gap-3">
        <span className="mt-0.5 flex size-8 shrink-0 items-center justify-center rounded-md border bg-muted/40">
          {phase === 'provisioning' ? (
            <Loader2 className="icon-sm animate-spin text-muted-foreground" aria-hidden />
          ) : phase === 'ready' ? (
            <Check className="icon-sm text-status-success" aria-hidden />
          ) : (
            <AlertCircle className="icon-sm text-status-accent-orange" aria-hidden />
          )}
        </span>
        {/* One stable live region for the whole body: the card swaps its copy
            in place rather than moving focus, and a region that only mounts
            with the new phase would never be announced. */}
        <div className="flex min-w-0 flex-1 flex-col gap-3" aria-live="polite">
          <div className="flex min-w-0 flex-col gap-1">
            <div className="flex min-w-0 items-center gap-2">
              <PanelTitle as="h3" size="sm" className="min-w-0">
                {TITLES[phase]}
              </PanelTitle>
              {docsSlug && <InlineDocsHint slug={docsSlug} />}
            </div>

            {phase === 'ready' ? (
              // Two lines rather than one sentence: the account summary comes
              // from a shared component that owns its own punctuation, so
              // running our copy on after it reads as one long run-on.
              <div
                className="flex flex-col gap-0.5 text-xs leading-snug text-muted-foreground/80"
                data-testid="sandbox-account-card-ready"
              >
                <RunloopConnectedAccount
                  account={account}
                  fallback="Your Runloop account is connected."
                />
                <span>
                  Reflex stored the key for you. Rotate it later from{' '}
                  <strong>Admin → Secrets</strong>.
                </span>
              </div>
            ) : (
              <p
                className="text-xs leading-snug text-muted-foreground/80"
                data-testid={
                  phase === 'failed'
                    ? 'sandbox-account-card-error'
                    : 'sandbox-account-card-provisioning'
                }
              >
                {message ??
                  (phase === 'failed' ? DEFAULT_FAILURE_MESSAGE : DEFAULT_PROVISIONING_MESSAGE)}
              </p>
            )}
          </div>

          {(showRetry || showManualKey) && (
            <div className="flex flex-wrap items-center gap-2">
              {showRetry && (
                <Button
                  size="sm"
                  onClick={onRetry}
                  disabled={isRetrying}
                  data-testid="sandbox-account-card-retry"
                >
                  {isRetrying ? 'Trying again…' : 'Try again'}
                </Button>
              )}
              {showManualKey && (
                <Button
                  size="sm"
                  variant="outline"
                  onClick={onAddKeyManually}
                  disabled={isRetrying}
                  data-testid="sandbox-account-card-manual-key"
                >
                  Use an existing Runloop key
                </Button>
              )}
            </div>
          )}
        </div>
      </div>
    </Card>
  );
}
